import Para from "@/atoms/text/Para";
import { AnywhereWorksLogo, BrandLogo, PPNBLogo } from "@/atoms/Logo";
import {
  LinkedInIcon,
  InstagramIcon,
  FacebookIcon,
  PlaystoreIcon,
  AppstoreIcon,
  PointArrowIcon,
  ChevronArrowIcon,
} from "@/atoms/Icons";
import { accordionHandler } from "@/utils/functions";
import useIntersectionObserver from "@/utils/useIntersectionObserver";

const AccordionList = ({ title, items }) => (
  <div className="accordion-list mb-5">
    <div className="fx-d fx--cv-d">
      <a href={title.href} className="tc-white text--mont text--md mr-1-d">
        {title.text}
      </a>
      <ChevronArrowIcon
        width="14"
        height="14"
        className="accordion-arrow"
        color="#fff"
        onClick={accordionHandler}
      />
    </div>
    <ul className="accordion--items pl-2">
      {items.map((item, index) => (
        <li key={index} className="tc-white mb-1">
          <a href={item.href} className="text text--md action--animline">
            {item.text}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const socialMediaLinksData = [
  {
    href: "https://www.linkedin.com/company/answerforce",
    icon: LinkedInIcon,
  },
  {
    href: "https://www.instagram.com/answerforce/",
    icon: InstagramIcon,
  },
  {
    href: "https://www.facebook.com/answerforce",
    icon: FacebookIcon,
  },
];

const footerLinksData = [
  { href: "/termsofuse", text: "Terms of Use" },
  {
    href: "/termsofservice",
    text: "Terms of Service",
  },
  {
    href: "/mediawebpolicy",
    text: "Media Web Policy",
  },
  { href: "/privacypolicy", text: "Privacy Policy" },
  { href: "/cookie-policy", text: "Cookies Policy" },
  { href: "/sitemap", text: "Sitemap" },
];

const footerAccordionData = [
  {
    title: { href: "/services", text: "Services" },
    items: [
      {
        href: "/answering-services",
        text: "24/7 Live Answering",
      },
      {
        href: "/live-chat",
        text: "Live Chat",
      },
      {
        href: "/answering-services/appointment-setting",
        text: "Appointment Scheduling",
      },
      {
        href: "/virtual-receptionist",
        text: "Virtual Receptionist",
      },
      {
        href: "/answering-services/bilingual-answering",
        text: "Bilingual Answering",
      },
      {
        href: "/answering-services/lead-capture",
        text: "Lead Qualification",
      },
    ],
  },
  {
    title: { href: "/industries", text: "Industries" },
    items: [
      { href: "/hvac-answering-service", text: "HVAC" },
      { href: "/restoration-answering-service", text: "Restoration" },
      { href: "/plumbing-answering-service", text: "Plumbing" },
      { href: "/electrician-answering-service", text: "Electrical" },
      { href: "/industries/handyman", text: "Handyman" },
      {
        href: "/contractors-answering-service",
        text: "Contractors",
      },
      { href: "/home-services", text: "Home services" },
      { href: "/industries/construction", text: "Construction" },
      { href: "/industries/solar", text: "Solar" },
      { href: "/industries/roofing", text: "Roofing" },
      {
        href: "/industries/property-maintenance",
        text: "Property maintenance",
      },
    ],
  },
  {
    title: { href: "/tools", text: "Tools" },
    items: [
      { href: "/live-chat", text: "Live Chat" },
      {
        href: "/answering-services/appointment-setting",
        text: "Appointment Scheduling",
      },
      { href: "/app", text: "Mobile App" },
      {
        href: "/business-phone-line",
        text: "Business Phone Line",
      },
    ],
  },
  {
    title: { href: "/integrations", text: "Integrations" },
    items: [
      { href: "/integrations/housecallpro", text: "Housecall Pro" },
      { href: "/integrations", text: "Service Minder" },
      { href: "/integrations", text: "Constant Contact" },
      { href: "/integrations", text: "FranConnect" },
      { href: "/integrations/servicetitan", text: "ServiceTitan" },
      { href: "/integrations/salesforce", text: "SalesForce" },
    ],
  },
  {
    title: {
      href: "/why",
      text: "Why AnswerForce",
    },
    items: [
      {
        href: "/case-studies",
        text: "Case Studies",
      },
      {
        href: "/giving-back",
        text: "Giving Back",
      },
      {
        href: "/pledge-people-not-bots",
        text: "Pledge People, Not Bots",
      },
      { href: "/grow-your-brand", text: "Grow your Brand" },
    ],
  },
  {
    title: { href: "/locations", text: "Locations" },
    items: [
      { href: "/answering-services/texas", text: "Texas" },
      { href: "/answering-services/california", text: "California" },
      { href: "/answering-services/denver", text: "Denver" },
      { href: "/answering-services/new-york", text: "New York" },
    ],
  },
];

export default function Footer({ isFooterMini = false }) {
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
  });

  return (
    <footer
      className={`footer w-100 bc-black ${isFooterMini ? "pv-5" : "pv-8"}`}
      ref={elementRef}
    >
      <div
        className={`${
          isFooterMini ? "" : "footer__nav"
        } container fx fx--c fx--sb`}
      >
        {!isFooterMini && (
          <>
            <div className="fx-d fx--sb mb-20-d">
              <div className="w-30-d mb-8 mb-reset-d">
                <a
                  href="/"
                  aria-label="Answerforce website"
                  rel="noopener noreferrer"
                  className="text text--sm mb-8-d mb-7"
                >
                  <BrandLogo color="white" />
                </a>
                {
                  <ul className="w-30-d mb-8-d mb-7 fx">
                    {socialMediaLinksData.map(({ href, icon: Icon }, index) => (
                      <li key={index} className="tc-white mr-5">
                        <a
                          href={href}
                          target="_blank"
                          className="text text--md"
                        >
                          <Icon className="social-media-icon" />
                        </a>
                      </li>
                    ))}
                  </ul>
                }
                <Para color="grey-dark" desktopSize="md" mobileSize="md">
                  because
                  <br />
                  <a
                    href="https://anywhereworks.com"
                    target="_blank"
                    aria-label="AnywhereWorks website"
                    rel="noopener noreferrer"
                    className="text text--sm"
                  >
                    <AnywhereWorksLogo width="140" height="18" />
                  </a>
                </Para>
              </div>
              <div className="accordion">
                {footerAccordionData.slice(0, 4).map((accordion, index) => (
                  <AccordionList
                    key={index}
                    title={accordion.title}
                    items={accordion.items}
                  />
                ))}
              </div>
              <div className="accordion">
                {footerAccordionData.slice(4).map((accordion, index) => (
                  <AccordionList
                    key={index}
                    title={accordion.title}
                    items={accordion.items}
                  />
                ))}
                <div className="mb-5">
                  <a
                    href="https://www.answerforce.com/blog/"
                    className="tc-white text text--md text--mont action--animline"
                  >
                    Resources
                  </a>
                </div>
                <div className="mb-5">
                  <a
                    href="/partners/"
                    className="tc-white text text--md text--mont action--animline"
                  >
                    Partners
                  </a>
                </div>
                <div className="mb-5 mb-reset-d">
                  <a
                    href="/referral"
                    className="tc-white text text--md text--mont action--animline"
                  >
                    Refer a Friend
                  </a>
                </div>
              </div>
              <div className="mb-8 mb-reset-d accordion">
                <div className="mb-5">
                  <a
                    href="/plans"
                    className="tc-white text text--md text--mont action--animline"
                  >
                    Plans & pricing
                  </a>
                </div>
                <div className="mb-5">
                  <a
                    id="Setmore_button_iframe"
                    href="https://answerforcedemo.setmore.com"
                    className="tc-white text text--md text--mont action--animline"
                  >
                    Schedule a demo
                  </a>
                </div>
                <div>
                  <a
                    href="/contact"
                    className="tc-white text text--md text--mont action--animline"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
            <div className="fx-d fx--sb mb-8-d">
              <div className="fx fx--cv w-70-d mb-8 mb-reset-d">
                <a
                  href="/pledge-people-not-bots"
                  aria-label="Answerforce website"
                  rel="noopener noreferrer"
                  className="text text--sm mr-3"
                >
                  <PPNBLogo />
                </a>
                <Para desktopSize="sm" mobileSize="sm" color="white">
                  We pledge to use real people
                  <br /> in customer interactions.
                </Para>
              </div>
              <Para
                color="grey-dark"
                desktopSize="md"
                mobileSize="md"
                className="mb-8 mb-reset-d mr-3-d"
              >
                Download the app
                <br />
                <a
                  href="https://play.google.com/store/apps/details?id=com.answerforce.mobile"
                  target="_blank"
                  className="text text--md mt-2 mr-5"
                >
                  <PlaystoreIcon className="download-app-icon" />
                </a>
                <a
                  href="https://apps.apple.com/us/app/answerforce/id1329496173"
                  target="_blank"
                  className="text text--md mt-2"
                >
                  <AppstoreIcon className="download-app-icon" />
                </a>
              </Para>
            </div>
          </>
        )}
        <div className="fx-d fx--sb">
          <div className="w-70-d mb-7 mb-reset-d">
            {
              <ul className="fx fx--w">
                <li className="tc-grey-dark mr-5-d mr-3 mb-1 mb-reset-d">
                  <Para
                    color="grey-dark"
                    desktopSize="sm"
                    mobileSize="sm"
                    className="text text--sm"
                  >
                    &#169; AnswerForce
                  </Para>
                </li>
                {footerLinksData.map((link, index) => (
                  <li
                    key={index}
                    className="tc-grey-dark mr-5-d mr-3 mb-1 mb-reset-d"
                  >
                    <a
                      href={link.href}
                      className="text text--sm action--animline"
                    >
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            }
          </div>
          <Para color="grey-dark" desktopSize="sm" mobileSize="sm">
            We are here: &nbsp;
            <a
              href="https://anywhereworks.com/blue-dot"
              className="text tc-white text--sm action--earth"
              rel="noopener noreferrer"
            >
              The Pale Blue Dot &nbsp;
              <PointArrowIcon />
              {isIntersecting && <span></span>}
            </a>
          </Para>
        </div>
      </div>
    </footer>
  );
}
